import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { executeRecaptcha } from "@/lib/recaptcha";
import { AdminHeader } from "@/components/platform/AdminHeader";

const FORMS = [["login", "Login"], ["register", "Register"], ["forgot", "Forgot password"]];

const Status = ({ cfg, domain }) => (
  <div className="card-surface space-y-3 p-6 text-sm" data-testid="recaptcha-status-card">
    <p className="flex items-center gap-2 font-semibold text-white"><ShieldCheck className="h-4 w-4 text-emerald-400" />Status</p>
    <p className={cfg.enabled ? "text-emerald-300" : "text-slate-400"} data-testid="recaptcha-status">{cfg.enabled ? "Active: protected forms are checked by Google reCAPTCHA v3." : "Off: forms are not checked."}</p>
    <p className="text-xs text-slate-500">Create v3 keys at google.com/recaptcha/admin. Add these domains to the key: {domain} and <span className="break-all text-slate-300">{window.location.hostname}</span>.</p>
    <p className="text-xs text-slate-500">When you turn it on and save, your keys are checked with Google first, so a wrong key can't lock you out.</p>
  </div>
);

export function PlatformRecaptcha({ path = "/admin/recaptcha", domain = "smartmbsarena.my", forms = FORMS }) {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const apply = (d) => { setCfg(d); setF({ enabled: d.enabled, site_key: d.site_key, secret: "", min_score: d.min_score, forms: d.forms }); };
  useEffect(() => {
    let live = true;
    api.get(path).then((r) => live && apply(r.data)).catch((e) => toast.error(formatErr(e)));
    return () => { live = false; };
  }, [path]);
  if (!f) return <Loader2 className="h-6 w-6 animate-spin text-emerald-400" data-testid="recaptcha-loading" />;

  const save = async () => {
    setBusy(true);
    try {
      let test_token = "";
      if (f.enabled) {
        try { test_token = await executeRecaptcha(f.site_key.trim(), "admin_test"); } catch { test_token = ""; }
      }
      apply((await api.put(path, { ...f, min_score: Number(f.min_score), test_token })).data);
      toast.success("reCAPTCHA settings saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-recaptcha">
      <AdminHeader title="reCAPTCHA Settings" sub="Google reCAPTCHA v3 (invisible) protects your forms from bots.">
        <Button className="rounded-full" onClick={save} disabled={busy} data-testid="recaptcha-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" />Save</>}
        </Button>
      </AdminHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="card-surface space-y-5 p-6" data-testid="recaptcha-form-card">
          <div className="flex items-center gap-3"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="recaptcha-enabled-switch" /><span className="text-sm text-white">Enable reCAPTCHA</span></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-400">Site Key</Label><Input value={f.site_key} maxLength={100} onChange={(e) => setF({ ...f, site_key: e.target.value })} data-testid="recaptcha-site-key-input" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Secret Key</Label>
            <Input type="password" autoComplete="new-password" value={f.secret} maxLength={100} placeholder={cfg.has_secret ? "Saved. Leave blank to keep it" : "Paste your secret key"} onChange={(e) => setF({ ...f, secret: e.target.value })} data-testid="recaptcha-secret-input" />
            <p className="text-xs text-slate-500" data-testid="recaptcha-secret-hint">{cfg.has_secret ? "A secret key is saved (stored encrypted)." : "No secret key saved yet."}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Minimum score (0.1 lenient to 0.9 strict)</Label>
            <Input type="number" min={0.1} max={0.9} step={0.1} className="w-32" value={f.min_score} onChange={(e) => setF({ ...f, min_score: e.target.value })} data-testid="recaptcha-min-score-input" />
            <p className="text-xs text-slate-500">0.5 is Google's recommended default.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Protected forms</Label>
            {forms.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-200">
                <Checkbox checked={!!f.forms[k]} onCheckedChange={(v) => setF({ ...f, forms: { ...f.forms, [k]: !!v } })} data-testid={`recaptcha-form-${k}`} />{l}
              </label>
            ))}
          </div>
        </div>
        <Status cfg={cfg} domain={domain} />
      </div>
    </div>
  );
}
